import React, { useState, useEffect, useCallback } from 'react';
import { Settings, Globe, CreditCard, Mail, Percent, Save, Bell, Shield, Loader2, IndianRupee, Clock, Calendar, Banknote, AlertCircle, CheckCircle, TrendingUp, Info, Play, FileAudio, Eye, EyeOff, Lock } from 'lucide-react';
import { motion } from 'framer-motion';
import DashboardLayout from '../components/dashboard/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth, API } from '../App';

const AdminSettings = () => {
    const { token, user } = useAuth();
    const [activeTab, setActiveTab] = useState('general');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
    const [showPasswords, setShowPasswords] = useState({ current: false, newPw: false, confirm: false })
    const [savingPassword, setSavingPassword] = useState(false);

    const [general, setGeneral] = useState({
        siteName: 'SolarSavers',
        tagline: 'Your Solar Energy Marketplace',
        email: 'admin@solarsavers.com',
        phone: '+91 98765 43210',
        address: ''
    });

    const [platformSettings, setPlatformSettings] = useState({
        default_commission_rate: 5.0,
        settlement_hold_days: 7,
        auto_settle_days: 28,
        min_payout_amount: 500,
        max_commission_rate: 50.0,
        payout_processing_note: 'Payouts are typically processed within 2-5 business days via bank transfer.'
    });

    const [notifications, setNotifications] = useState({
        newOrders: true,
        newVendors: true,
        lowStock: true,
        weeklyReport: true
    });

    const headers = { Authorization: `Bearer ${token}` };

    const fetchSettings = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/api/admin/platform-settings`, { headers });
            setPlatformSettings(prev => ({ ...prev, ...res.data }));
            if (res.data.notification_settings) {
                setNotifications(prev => ({ ...prev, ...res.data.notification_settings }));
            }
        } catch (e) {
            // Will use defaults
        }
        setLoading(false);
    }, [token]);

    useEffect(() => {
        if (token) fetchSettings();
    }, [token]);

    const handleSaveGeneral = () => {
        toast.success('General settings saved successfully!');
    };

    const handleSaveCommission = async () => {
        setSaving(true);
        try {
            await axios.put(`${API}/api/admin/platform-settings`, {
                default_commission_rate: parseFloat(platformSettings.default_commission_rate),
                settlement_hold_days: parseInt(platformSettings.settlement_hold_days),
                auto_settle_days: parseInt(platformSettings.auto_settle_days),
                min_payout_amount: parseFloat(platformSettings.min_payout_amount),
                max_commission_rate: parseFloat(platformSettings.max_commission_rate),
                payout_processing_note: platformSettings.payout_processing_note,
            }, { headers });
            toast.success('Payment & Commission settings saved!');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to save settings');
        }
        setSaving(false);
    };

    const handleSaveNotifications = async () => {
        try {
            await axios.put(`${API}/api/admin/platform-settings`, {
                notification_settings: notifications
            }, { headers });
            toast.success('Notification settings saved!');
        } catch (e) {
            toast.error('Failed to save notification settings');
        }
    };

    const handleAudioUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (!file.type.startsWith('audio/')) {
            return toast.error("Please select a valid audio file (mp3, wav)");
        }

        const formData = new FormData();
        formData.append('file', file);
        setSaving(true);
        try {
            const uploadRes = await axios.post(`${API}/api/upload/public`, formData, { headers });
            const audioUrl = uploadRes.data.url;
            
            await axios.put(`${API}/api/auth/profile`, { 
                notification_sound_url: audioUrl 
            }, { headers });
            
            toast.success("Notification sound updated! (Refresh to apply everywhere)");
            setPlatformSettings(prev => ({ ...prev, notification_sound_url: audioUrl }));
        } catch (err) {
            toast.error("Failed to upload audio file");
        }
        setSaving(false);
    };

    const playTestSound = () => {
        const soundUrl = user?.notification_sound_url || platformSettings?.notification_sound_url;
        if (soundUrl) {
            new Audio(API + soundUrl).play().catch(e => toast.error("Browser blocked audio playback"));
        } else {
            toast.info("No custom sound currently set. The default synthetic chime will play.");
        }
    };

    const handleChangePassword = async () => {
        if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
            return toast.error('Please fill in all password fields.');
        }
        if (passwordForm.newPassword !== passwordForm.confirmPassword) {
            return toast.error('New passwords do not match.');
        }
        if (passwordForm.newPassword.length < 8) {
            return toast.error('New password must be at least 8 characters.');
        }
        setSavingPassword(true);
        try {
            await axios.put(`${API}/api/auth/change-password`, {
                current_password: passwordForm.currentPassword,
                new_password: passwordForm.newPassword
            }, { headers });
            toast.success('Password changed successfully!');
            setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Failed to change password.');
        }
        setSavingPassword(false);
    };

    // Calculate preview values
    const previewSaleAmount = 100000;
    const commRate = parseFloat(platformSettings.default_commission_rate) || 0;
    const platformEarns = Math.round(previewSaleAmount * commRate / 100);
    const vendorEarns = previewSaleAmount - platformEarns;

    const tabs = [
        { id: 'general', label: 'General', icon: Globe },
        { id: 'commission', label: 'Payment & Commission', icon: Percent },
        { id: 'notifications', label: 'Notifications', icon: Bell },
        { id: 'security', label: 'Security', icon: Shield }
    ];

    if (loading) {
        return (
            <DashboardLayout userRole="admin">
                <div className="flex items-center justify-center h-64">
                    <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout userRole="admin">
            <div className="space-y-6">
                <div>
                    <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2">
                        <Settings className="w-8 h-8 text-primary" />
                        Platform Settings
                    </h1>
                    <p className="text-muted-foreground mt-1">Configure marketplace settings, commission rates, and payment policies</p>
                </div>

                <div className="flex gap-1 p-1 bg-muted/50 rounded-xl overflow-x-auto">
                    {tabs.map((tab) => {
                        const isActive = activeTab === tab.id;
                        return (
                            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium text-sm transition-all whitespace-nowrap ${isActive
                                    ? 'bg-background text-foreground shadow-sm'
                                    : 'text-muted-foreground hover:text-foreground'
                                    }`}>
                                <tab.icon className="w-4 h-4" />
                                {tab.label}
                            </button>
                        );
                    })}
                </div>

                <motion.div key={activeTab} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                    {/* ── GENERAL TAB ── */}
                    {activeTab === 'general' && (
                        <div className="bg-card rounded-xl border p-6 space-y-6">
                            <h3 className="font-semibold text-lg flex items-center gap-2"><Globe className="w-5 h-5 text-primary" /> General Settings</h3>
                            <div className="grid md:grid-cols-2 gap-4">
                                <div className="space-y-2"><Label>Site Name</Label><Input value={general.siteName} onChange={(e) => setGeneral({ ...general, siteName: e.target.value })} /></div>
                                <div className="space-y-2"><Label>Tagline</Label><Input value={general.tagline} onChange={(e) => setGeneral({ ...general, tagline: e.target.value })} /></div>
                                <div className="space-y-2"><Label>Admin Email</Label><Input type="email" value={general.email} onChange={(e) => setGeneral({ ...general, email: e.target.value })} /></div>
                                <div className="space-y-2"><Label>Phone</Label><Input value={general.phone} onChange={(e) => setGeneral({ ...general, phone: e.target.value })} /></div>
                            </div>
                            <div className="space-y-2"><Label>Address</Label><Textarea value={general.address} onChange={(e) => setGeneral({ ...general, address: e.target.value })} /></div>
                            <Button onClick={handleSaveGeneral} className="btn-primary"><Save className="w-4 h-4 mr-2" /> Save Changes</Button>
                        </div>
                    )}

                    {/* ── PAYMENT & COMMISSION TAB ── */}
                    {activeTab === 'commission' && (
                        <div className="space-y-6">
                            {/* Commission Rate */}
                            <div className="bg-card rounded-xl border overflow-hidden">
                                <div className="p-5 border-b bg-gradient-to-r from-primary/5 to-violet-500/5">
                                    <h3 className="font-semibold text-lg flex items-center gap-2">
                                        <Percent className="w-5 h-5 text-primary" />
                                        Default Commission Rate
                                    </h3>
                                    <p className="text-sm text-muted-foreground mt-1">Platform commission deducted from every vendor sale. Individual vendors can have custom rates.</p>
                                </div>
                                <div className="p-5 space-y-5">
                                    <div className="grid md:grid-cols-2 gap-6">
                                        <div className="space-y-3">
                                            <Label className="text-sm font-semibold">Commission Rate (%)</Label>
                                            <div className="relative">
                                                <Input
                                                    type="number"
                                                    min="0" max="50" step="0.5"
                                                    value={platformSettings.default_commission_rate}
                                                    onChange={(e) => setPlatformSettings({ ...platformSettings, default_commission_rate: e.target.value })}
                                                    className="text-2xl font-bold h-14 pr-12"
                                                />
                                                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground font-bold text-lg">%</span>
                                            </div>
                                            <div className="flex gap-2">
                                                {[3, 5, 8, 10, 15].map(v => (
                                                    <button key={v} onClick={() => setPlatformSettings({ ...platformSettings, default_commission_rate: v })}
                                                        className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${parseFloat(platformSettings.default_commission_rate) === v ? 'bg-primary text-white border-primary' : 'bg-muted/30 hover:bg-muted/60 border-transparent'}`}>
                                                        {v}%
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Live Preview */}
                                        <div className="bg-gradient-to-br from-primary/5 to-violet-500/5 rounded-xl p-5 border border-primary/10">
                                            <p className="text-xs font-semibold text-muted-foreground uppercase mb-3">Preview: ₹{previewSaleAmount.toLocaleString()} Sale</p>
                                            <div className="space-y-2.5">
                                                <div className="flex justify-between items-center">
                                                    <span className="text-sm text-muted-foreground">Customer Pays</span>
                                                    <span className="font-bold font-mono">₹{previewSaleAmount.toLocaleString()}</span>
                                                </div>
                                                <div className="flex justify-between items-center">
                                                    <span className="text-sm text-violet-600 flex items-center gap-1"><TrendingUp className="w-3.5 h-3.5" /> Platform Commission ({commRate}%)</span>
                                                    <span className="font-bold font-mono text-violet-600">₹{platformEarns.toLocaleString()}</span>
                                                </div>
                                                <div className="border-t pt-2 flex justify-between items-center">
                                                    <span className="text-sm font-medium text-green-600">Vendor Earns</span>
                                                    <span className="font-bold font-mono text-green-600 text-lg">₹{vendorEarns.toLocaleString()}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Settlement Policy */}
                            <div className="bg-card rounded-xl border overflow-hidden">
                                <div className="p-5 border-b bg-gradient-to-r from-amber-500/5 to-orange-500/5">
                                    <h3 className="font-semibold text-lg flex items-center gap-2">
                                        <Clock className="w-5 h-5 text-amber-600" />
                                        Settlement Policy
                                    </h3>
                                    <p className="text-sm text-muted-foreground mt-1">Control when vendor funds become available and auto-settlement timelines</p>
                                </div>
                                <div className="p-5">
                                    <div className="grid md:grid-cols-3 gap-5">
                                        <div className="space-y-2">
                                            <Label className="flex items-center gap-1.5 text-sm font-semibold">
                                                <Clock className="w-3.5 h-3.5 text-amber-500" />
                                                Hold Period (Days)
                                            </Label>
                                            <Input
                                                type="number" min="0" max="90"
                                                value={platformSettings.settlement_hold_days}
                                                onChange={(e) => setPlatformSettings({ ...platformSettings, settlement_hold_days: e.target.value })}
                                            />
                                            <p className="text-xs text-muted-foreground">Funds are held for this many days after order delivery before becoming available to vendor</p>
                                        </div>
                                        <div className="space-y-2">
                                            <Label className="flex items-center gap-1.5 text-sm font-semibold">
                                                <Calendar className="w-3.5 h-3.5 text-blue-500" />
                                                Auto-Settlement (Days)
                                            </Label>
                                            <Input
                                                type="number" min="1" max="180"
                                                value={platformSettings.auto_settle_days}
                                                onChange={(e) => setPlatformSettings({ ...platformSettings, auto_settle_days: e.target.value })}
                                            />
                                            <p className="text-xs text-muted-foreground">Automatically create payout for vendors after this many days from delivery</p>
                                        </div>
                                        <div className="space-y-2">
                                            <Label className="flex items-center gap-1.5 text-sm font-semibold">
                                                <IndianRupee className="w-3.5 h-3.5 text-green-500" />
                                                Minimum Payout (₹)
                                            </Label>
                                            <Input
                                                type="number" min="0"
                                                value={platformSettings.min_payout_amount}
                                                onChange={(e) => setPlatformSettings({ ...platformSettings, min_payout_amount: e.target.value })}
                                            />
                                            <p className="text-xs text-muted-foreground">Vendors must reach this balance before requesting a payout</p>
                                        </div>
                                    </div>

                                    {/* Visual Settlement Timeline */}
                                    <div className="mt-6 p-4 bg-muted/20 rounded-xl border">
                                        <p className="text-xs font-semibold text-muted-foreground uppercase mb-3">Settlement Timeline Preview</p>
                                        <div className="flex items-center gap-0 overflow-x-auto">
                                            <div className="flex flex-col items-center min-w-[100px]">
                                                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center"><Banknote className="w-5 h-5 text-blue-600" /></div>
                                                <p className="text-xs font-medium mt-1.5">Order Delivered</p>
                                                <p className="text-[10px] text-muted-foreground">Day 0</p>
                                            </div>
                                            <div className="flex-1 h-0.5 bg-gradient-to-r from-blue-300 to-amber-300 min-w-[40px]" />
                                            <div className="flex flex-col items-center min-w-[120px]">
                                                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center"><Clock className="w-5 h-5 text-amber-600" /></div>
                                                <p className="text-xs font-medium mt-1.5">Hold Ends</p>
                                                <p className="text-[10px] text-amber-600 font-mono">Day {platformSettings.settlement_hold_days}</p>
                                            </div>
                                            <div className="flex-1 h-0.5 bg-gradient-to-r from-amber-300 to-green-300 min-w-[40px]" />
                                            <div className="flex flex-col items-center min-w-[120px]">
                                                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center"><CheckCircle className="w-5 h-5 text-green-600" /></div>
                                                <p className="text-xs font-medium mt-1.5">Vendor Can Withdraw</p>
                                                <p className="text-[10px] text-green-600 font-mono">Day {platformSettings.settlement_hold_days}+</p>
                                            </div>
                                            <div className="flex-1 h-0.5 bg-gradient-to-r from-green-300 to-violet-300 min-w-[40px]" />
                                            <div className="flex flex-col items-center min-w-[120px]">
                                                <div className="w-10 h-10 rounded-full bg-violet-100 flex items-center justify-center"><TrendingUp className="w-5 h-5 text-violet-600" /></div>
                                                <p className="text-xs font-medium mt-1.5">Auto-Settlement</p>
                                                <p className="text-[10px] text-violet-600 font-mono">Day {platformSettings.auto_settle_days}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Payout Note */}
                            <div className="bg-card rounded-xl border overflow-hidden">
                                <div className="p-5 border-b">
                                    <h3 className="font-semibold text-lg flex items-center gap-2">
                                        <Info className="w-5 h-5 text-blue-500" />
                                        Payout Processing Note
                                    </h3>
                                    <p className="text-sm text-muted-foreground mt-1">This message is shown to vendors on their payout request screen</p>
                                </div>
                                <div className="p-5">
                                    <Textarea
                                        value={platformSettings.payout_processing_note}
                                        onChange={(e) => setPlatformSettings({ ...platformSettings, payout_processing_note: e.target.value })}
                                        rows={3}
                                        placeholder="e.g. Payouts are processed within 2-5 business days..."
                                    />
                                </div>
                            </div>

                            {/* Save Button */}
                            <div className="flex items-center justify-between p-4 bg-card rounded-xl border">
                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                    <AlertCircle className="w-4 h-4" />
                                    Changes apply to all new vendors. Existing vendor commission rates are not affected.
                                </div>
                                <Button onClick={handleSaveCommission} disabled={saving} className="btn-primary gap-2">
                                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                                    Save Payment Settings
                                </Button>
                            </div>
                        </div>
                    )}

                    {/* ── NOTIFICATIONS TAB ── */}
                    {activeTab === 'notifications' && (
                        <div className="bg-card rounded-xl border p-6 space-y-6">
                            <h3 className="font-semibold text-lg flex items-center gap-2"><Bell className="w-5 h-5 text-primary" /> Notification Settings</h3>
                            
                            <div className="bg-muted/30 p-5 rounded-lg border border-border/50">
                                <h4 className="font-medium text-sm text-foreground/80 mb-4 flex items-center gap-2">
                                    <FileAudio className="w-4 h-4 text-indigo-500" /> Custom Notification Sound
                                </h4>
                                <div className="flex flex-col md:flex-row items-center gap-4">
                                    <Input 
                                        type="file" 
                                        accept="audio/mp3,audio/wav,audio/ogg" 
                                        onChange={handleAudioUpload}
                                        disabled={saving}
                                        className="bg-background cursor-pointer max-w-sm"
                                    />
                                    <Button onClick={playTestSound} variant="outline" className="gap-2">
                                        <Play className="w-4 h-4" /> Play Test
                                    </Button>
                                </div>
                                <p className="text-xs text-muted-foreground mt-3">
                                    Upload an audio file (MP3, WAV) to override the default system chime. This sound is specific to your account across all login devices.
                                </p>
                            </div>

                            <div className="space-y-4">
                                {[
                                    { key: 'newOrders', label: 'New Orders', desc: 'Get notified when new orders are placed' },
                                    { key: 'newVendors', label: 'New Vendors', desc: 'Get notified when vendors apply' },
                                    { key: 'lowStock', label: 'Low Stock Alerts', desc: 'Get notified when products are low on stock' },
                                    { key: 'tickets', label: 'Support Tickets', desc: 'Get notified on any inquiry from vendor or customer ticket system' },
                                    { key: 'expertVisits', label: 'Expert Visits', desc: 'Get notified when customers request an expert visit' }
                                ].map((item) => (
                                    <div key={item.key} className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
                                        <div><p className="font-medium">{item.label}</p><p className="text-sm text-muted-foreground">{item.desc}</p></div>
                                        <label className="relative inline-flex items-center cursor-pointer">
                                            <input type="checkbox" checked={notifications[item.key]} onChange={(e) => setNotifications({ ...notifications, [item.key]: e.target.checked })} className="sr-only peer" />
                                            <div className="w-11 h-6 bg-gray-200 peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                                        </label>
                                    </div>
                                ))}
                            </div>
                            <Button onClick={handleSaveNotifications} className="btn-primary"><Save className="w-4 h-4 mr-2" /> Save Preferences</Button>
                        </div>
                    )}

                    {/* ── SECURITY TAB ── */}
                    {activeTab === 'security' && (
                        <div className="space-y-6">
                            {/* SSL Status */}
                            <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl flex items-center gap-3">
                                <CheckCircle className="w-5 h-5 text-green-600 shrink-0" />
                                <div>
                                    <p className="font-medium text-green-800 dark:text-green-400">SSL Certificate Active</p>
                                    <p className="text-sm text-green-700 dark:text-green-500">Your site is secured with HTTPS encryption</p>
                                </div>
                            </div>

                            {/* Change Password Card */}
                            <div className="bg-card rounded-xl border p-6 space-y-5">
                                <h3 className="font-semibold text-lg flex items-center gap-2">
                                    <Lock className="w-5 h-5 text-primary" /> Change Password
                                </h3>
                                <p className="text-sm text-muted-foreground -mt-3">Update your admin account password. Use at least 8 characters.</p>

                                <div className="space-y-4 max-w-md">
                                    {/* Current Password */}
                                    <div className="space-y-1.5">
                                        <Label htmlFor="current-password">Current Password</Label>
                                        <div className="relative">
                                            <Input
                                                id="current-password"
                                                type={showPasswords.current ? 'text' : 'password'}
                                                placeholder="Enter current password"
                                                value={passwordForm.currentPassword}
                                                onChange={e => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                                                className="pr-10"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowPasswords(s => ({ ...s, current: !s.current }))}
                                                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                            >
                                                {showPasswords.current ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                            </button>
                                        </div>
                                    </div>

                                    {/* New Password */}
                                    <div className="space-y-1.5">
                                        <Label htmlFor="new-password">New Password</Label>
                                        <div className="relative">
                                            <Input
                                                id="new-password"
                                                type={showPasswords.newPw ? 'text' : 'password'}
                                                placeholder="Enter new password (min. 8 chars)"
                                                value={passwordForm.newPassword}
                                                onChange={e => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                                                className="pr-10"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowPasswords(s => ({ ...s, newPw: !s.newPw }))}
                                                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                            >
                                                {showPasswords.newPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                            </button>
                                        </div>
                                    </div>

                                    {/* Confirm New Password */}
                                    <div className="space-y-1.5">
                                        <Label htmlFor="confirm-password">Confirm New Password</Label>
                                        <div className="relative">
                                            <Input
                                                id="confirm-password"
                                                type={showPasswords.confirm ? 'text' : 'password'}
                                                placeholder="Re-enter new password"
                                                value={passwordForm.confirmPassword}
                                                onChange={e => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                                                className="pr-10"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowPasswords(s => ({ ...s, confirm: !s.confirm }))}
                                                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                            >
                                                {showPasswords.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                            </button>
                                        </div>
                                    </div>

                                    {/* Strength hint */}
                                    {passwordForm.newPassword && (
                                        <p className={`text-xs font-medium ${
                                            passwordForm.newPassword.length >= 12 ? 'text-green-600' :
                                            passwordForm.newPassword.length >= 8 ? 'text-amber-600' : 'text-red-500'
                                        }`}>
                                            {passwordForm.newPassword.length >= 12 ? '✔ Strong password' :
                                             passwordForm.newPassword.length >= 8 ? '⚠ Acceptable – consider longer' : '✗ Too short (min 8 characters)'}
                                        </p>
                                    )}

                                    <Button
                                        onClick={handleChangePassword}
                                        disabled={savingPassword}
                                        className="gap-2 w-full sm:w-auto"
                                    >
                                        {savingPassword ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
                                        Update Password
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}
                </motion.div>
            </div>
        </DashboardLayout>
    );
};

export default AdminSettings;
